'use client';

import React, { useState, useMemo } from 'react';
import {
    PieChart,
    TrendingUp,
    TrendingDown,
    Calendar,
    ChevronDown,
    Printer,
    FileDown,
    ArrowUpRight,
    ArrowDownRight,
    Info,
    LayoutDashboard,
    ArrowRight,
    Banknote
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

interface IncomeStatementItem {
    accountCode: string;
    accountName: string;
    amount: number;
}

interface IncomeStatementData {
    period: string;
    unit: string;
    income: IncomeStatementItem[];
    expense: IncomeStatementItem[];
}

// --- Mock Data ---

const MOCK_DATA: IncomeStatementData = {
    period: 'Februari 2024',
    unit: 'PAUD Al-Azhar 1',
    income: [
        { accountCode: '4-101', accountName: 'Pendapatan SPP', amount: 45000000 },
        { accountCode: '4-102', accountName: 'Pendapatan Pendaftaran', amount: 5000000 },
        { accountCode: '4-201', accountName: 'Donasi Yayasan', amount: 10000000 },
        { accountCode: '4-301', accountName: 'Pendapatan Lainnya', amount: 1500000 }
    ],
    expense: [
        { accountCode: '5-101', accountName: 'Beban Gaji & Honor Guru', amount: 25000000 },
        { accountCode: '5-102', accountName: 'Beban ATK & Konsumsi', amount: 3500000 },
        { accountCode: '5-201', accountName: 'Beban Sarana & Prasarana', amount: 8000000 },
        { accountCode: '5-301', accountName: 'Beban Kegiatan Siswa', amount: 12000000 },
        { accountCode: '5-401', accountName: 'Beban Utilitas (Air/Listrik)', amount: 2000000 }
    ]
};

export default function LaporanLabaRugiPage() {
    // --- State ---
    const [periodType, setPeriodType] = useState<'Bulanan' | 'Tahunan'>('Bulanan');
    const [compareWithPrevious, setCompareWithPrevious] = useState(false);

    // --- Calculations ---
    const totals = useMemo(() => {
        const totalIncome = MOCK_DATA.income.reduce((sum, item) => sum + item.amount, 0);
        const totalExpense = MOCK_DATA.expense.reduce((sum, item) => sum + item.amount, 0);
        const netProfit = totalIncome - totalExpense;
        return { totalIncome, totalExpense, netProfit };
    }, []);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                            <Link href="/sekolah" className="hover:text-primary transition-all">Laporan Keuangan</Link>
                            <span className="text-muted/50">•</span>
                            <span className="text-foreground">Laba Rugi</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <PieChart className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight whitespace-nowrap">Laporan Laba Rugi</h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-wider">
                                    <Banknote className="w-3.5 h-3.5 text-success" /> Ringkasan Pendapatan & Beban • {MOCK_DATA.unit}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-primary/30 transition-all shadow-sm group">
                            <FileDown className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" /> Export Excel
                        </button>
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">
                            <Printer className="w-4 h-4" /> Export PDF
                        </button>
                    </div>
                </div>
            </section>

            {/* Filter & Period Selector */}
            <div className="bg-surface border border-border-main p-6 rounded-3xl shadow-sm space-y-6">
                <div className="flex flex-wrap items-center gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Jenis Periode</label>
                        <div className="flex bg-background p-1.5 rounded-2xl border border-border-main w-fit">
                            <button
                                onClick={() => setPeriodType('Bulanan')}
                                className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${periodType === 'Bulanan' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted hover:text-foreground'}`}
                            >
                                Bulanan
                            </button>
                            <button
                                onClick={() => setPeriodType('Tahunan')}
                                className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${periodType === 'Tahunan' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted hover:text-foreground'}`}
                            >
                                Tahunan
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2 flex-1 md:max-w-[240px]">
                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Periode Laporan</label>
                        <button className="w-full flex items-center justify-between px-5 py-3.5 bg-background border border-border-main rounded-2xl text-[11px] font-bold text-foreground">
                            <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-primary" />
                                {periodType === 'Bulanan' ? 'Februari 2024' : 'Tahun Ajaran 2024/2025'}
                            </div>
                            <ChevronDown className="w-4 h-4 text-muted" />
                        </button>
                    </div>

                    <div className="flex items-center gap-3 mt-auto mb-1">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={compareWithPrevious}
                                onChange={(e) => setCompareWithPrevious(e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-muted/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                        <span className="text-[11px] font-bold text-secondary uppercase tracking-wider">Bandingkan Periode Lalu</span>
                    </div>
                </div>
            </div>

            {/* Main Content Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Income Section */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between px-4">
                        <h3 className="text-xl font-black text-foreground flex items-center gap-3">
                            <div className="p-2 bg-success-soft rounded-xl text-success"><TrendingUp className="w-5 h-5" /></div>
                            Pendapatan
                        </h3>
                        {compareWithPrevious && <span className="text-[10px] font-black text-muted uppercase tracking-widest">Vs Lalu</span>}
                    </div>

                    <div className="bg-surface border border-border-main rounded-4xl overflow-hidden shadow-sm">
                        <div className="p-6 space-y-4">
                            {MOCK_DATA.income.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between group">
                                    <div className="space-y-1">
                                        <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{item.accountName}</p>
                                        <p className="text-[10px] font-medium text-muted uppercase font-mono">{item.accountCode}</p>
                                    </div>
                                    <div className="text-right flex items-center gap-6">
                                        {compareWithPrevious && <span className="text-[10px] font-bold text-success">+12%</span>}
                                        <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(item.amount)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="bg-success-soft/30 p-6 border-t border-success/10 flex items-center justify-between">
                            <p className="text-[10px] font-black text-success uppercase tracking-[0.2em]">Total Pendapatan</p>
                            <p className="text-xl font-black text-success tabular-nums">{formatCurrency(totals.totalIncome)}</p>
                        </div>
                    </div>
                </div>

                {/* Expense Section */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between px-4">
                        <h3 className="text-xl font-black text-foreground flex items-center gap-3">
                            <div className="p-2 bg-error-soft rounded-xl text-error"><TrendingDown className="w-5 h-5" /></div>
                            Beban Operasional
                        </h3>
                        {compareWithPrevious && <span className="text-[10px] font-black text-muted uppercase tracking-widest">Vs Lalu</span>}
                    </div>

                    <div className="bg-surface border border-border-main rounded-4xl overflow-hidden shadow-sm">
                        <div className="p-6 space-y-4">
                            {MOCK_DATA.expense.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between group">
                                    <div className="space-y-1">
                                        <p className="text-sm font-bold text-foreground group-hover:text-error transition-colors">{item.accountName}</p>
                                        <p className="text-[10px] font-medium text-muted uppercase font-mono">{item.accountCode}</p>
                                    </div>
                                    <div className="text-right flex items-center gap-6">
                                        {compareWithPrevious && <span className="text-[10px] font-bold text-error">+5%</span>}
                                        <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(item.amount)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="bg-error-soft/30 p-6 border-t border-error/10 flex items-center justify-between">
                            <p className="text-[10px] font-black text-error uppercase tracking-[0.2em]">Total Beban</p>
                            <p className="text-xl font-black text-error tabular-nums">{formatCurrency(totals.totalExpense)}</p>
                        </div>
                    </div>
                </div>

            </div>

            {/* Bottom Summary Footer */}
            <div className="bg-primary text-white rounded-[40px] p-8 md:p-12 shadow-2xl shadow-primary/20 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-700"></div>

                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-10">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-md">
                                <LayoutDashboard className="w-6 h-6" />
                            </div>
                            <h4 className="text-xl font-bold uppercase tracking-widest">Ringkasan Kinerja</h4>
                        </div>
                        <p className="text-primary-soft text-sm font-medium leading-relaxed max-w-sm">
                            Kinerja keuangan sekolah menunjukkan trend positif dengan margin operasional yang terjaga dengan baik periode ini.
                        </p>
                    </div>

                    <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-center">
                        <div className="text-center md:text-right space-y-1">
                            <p className="text-[10px] font-black text-primary-soft uppercase tracking-[0.3em]">Surplus / (Defisit)</p>
                            <h2 className={`text-4xl md:text-5xl font-black tabular-nums transition-all ${totals.netProfit < 0 ? 'text-error-soft' : 'text-white'}`}>
                                {formatCurrency(totals.netProfit)}
                            </h2>
                            <div className="flex items-center justify-center md:justify-end gap-2 text-primary-soft font-bold text-[10px] mt-2">
                                {totals.netProfit >= 0 ? <ArrowUpRight className="w-4 h-4 text-success-soft" /> : <ArrowDownRight className="w-4 h-4 text-error-soft" />}
                                {totals.netProfit >= 0 ? 'LABA BERSIH OPERASIONAL' : 'RUGI BERSIH OPERASIONAL'}
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button className="flex items-center gap-3 px-8 py-4 bg-white text-primary rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:scale-[1.05] transition-all">
                                Lihat Analisis <ArrowRight className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-2 justify-center text-[9px] font-black text-primary-soft opacity-60">
                                <Info className="w-3 h-3" /> DATA TERVALIDASI SISTEM
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </main>
    );
}
